
import { Card } from "@/components/ui/card";
import { Collection } from "@/lib/api/types";
import { CollectionCards } from "@/components/collections/CollectionCards";
import { CollectionSelector } from "@/components/collections/CollectionSelector";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { AggregatedDownloadButton } from "./AggregatedDownloadButton";
import { useAggregatedDownload } from "@/hooks/useAggregatedDownload";

interface DashboardCollectionsProps {
  collections: Collection[];
  selectedCollection: string | undefined;
  setSelectedCollection: (id: string | undefined) => void;
  onCreateCollection: () => void;
  onEdit: (collection: Collection) => void;
  onDelete: (collection: Collection) => void;
}

export const DashboardCollections = ({
  collections,
  selectedCollection,
  setSelectedCollection,
  onCreateCollection,
  onEdit,
  onDelete,
}: DashboardCollectionsProps) => {
  const navigate = useNavigate();

  const handleCreateCollection = () => {
    navigate('/collections');
    onCreateCollection();
  };

  // Add detailed logging to understand the collections structure
  console.log('DashboardCollections: All collections:', collections);
  console.log('DashboardCollections: Collections count:', collections?.length || 0);

  // Filter collections to only show those where ALL product descriptions are approved
  const fullyApprovedCollections = collections.filter(collection => {
    console.log(`Checking collection "${collection.name}":`, {
      id: collection.id,
      product_descriptions: collection.product_descriptions,
      product_descriptions_length: collection.product_descriptions?.length || 0
    });
    
    // Check if this collection has product descriptions
    if (!collection.product_descriptions || collection.product_descriptions.length === 0) {
      console.log(`  Collection "${collection.name}" has no product descriptions`);
      return false;
    }

    // Check if ALL product descriptions are approved
    const allApproved = collection.product_descriptions.every((desc: any) => {
      console.log(`  Description status: ${desc.status}`);
      return desc.status === 'approved';
    });
    
    console.log(`  Collection "${collection.name}" all descriptions approved: ${allApproved}`);
    return allApproved;
  });

  // Filter collections that have at least some approved descriptions for aggregated downloads
  const collectionsWithApprovedDescriptions = collections.filter(collection => {
    return collection.product_descriptions?.some((desc: any) => desc.status === 'approved') || false;
  });

  console.log('DashboardCollections: Fully approved collections:', fullyApprovedCollections);
  console.log('DashboardCollections: Fully approved collections count:', fullyApprovedCollections.length);
  console.log('DashboardCollections: Collections with some approved descriptions:', collectionsWithApprovedDescriptions.length);

  return (
    <>
      {/* Always show aggregated download section */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Aggregated Downloads</h2>
        <Card className="p-6">
          <h3 className="text-lg font-medium mb-4">Download Combined Data</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Download collections that include both original descriptions and all approved translations in a single file.
          </p>
          {collectionsWithApprovedDescriptions.length > 0 ? (
            <div className="grid gap-4">
              {collectionsWithApprovedDescriptions.map(collection => (
                <AggregatedDownloadRow key={collection.id} collection={collection} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-2">No collections with approved descriptions yet.</p>
              <p className="text-sm text-muted-foreground">
                Create a collection and generate approved descriptions to enable aggregated downloads.
              </p>
            </div>
          )}
        </Card>
      </div>

      {fullyApprovedCollections && fullyApprovedCollections.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Approved Collections</h2>
          <CollectionCards
            collections={fullyApprovedCollections}
            selectedCollectionId={undefined}
            onNavigate={(id) => navigate(`/collections/${id}`)}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
          <div className="space-y-4">
            <Button 
              className="w-full"
              onClick={handleCreateCollection}
            >
              Create New Collection
            </Button>
            {collections && collections.length > 0 && (
              <Button 
                variant="secondary"
                className="w-full"
                onClick={() => navigate('/collections')}
              >
                View Generated Content
              </Button>
            )}
            <Button 
              variant="secondary" 
              className="w-full"
              onClick={() => navigate('/settings')}
            >
              Manage Settings
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
};

// Component to show individual collection download status
const AggregatedDownloadRow = ({ collection }: { collection: Collection }) => {
  const { isEligible, isLoading } = useAggregatedDownload(collection.id);

  console.log(`AggregatedDownloadRow for ${collection.name}: isEligible=${isEligible}, isLoading=${isLoading}`);

  if (isLoading) {
    return (
      <div className="flex items-center justify-between p-3 border rounded-lg">
        <div>
          <h4 className="font-medium">{collection.name}</h4>
          <p className="text-sm text-muted-foreground">Checking eligibility...</p>
        </div>
        <div className="animate-pulse bg-muted h-10 w-32 rounded"></div>
      </div>
    );
  }

  if (!isEligible) {
    return (
      <div className="flex items-center justify-between p-3 border-muted-foreground/30 rounded-lg bg-muted/50">
        <div>
          <h4 className="font-medium text-muted-foreground">{collection.name}</h4>
          <p className="text-sm text-muted-foreground">
            Not eligible - missing approved translations
          </p>
        </div>
        <Button variant="outline" disabled className="opacity-50">
          Not Available
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-3 border rounded-lg">
      <div>
        <h4 className="font-medium">{collection.name}</h4>
        <p className="text-sm text-muted-foreground">
          Ready for aggregated download
        </p>
      </div>
      <AggregatedDownloadButton 
        collectionId={collection.id} 
        collectionName={collection.name}
      />
    </div>
  );
};
